/**
 * GST Service
 * Handles communication with Supabase Edge Functions for Whitebook API integration
 * Migrated from GSTZen to Whitebook/MasterGST APIs for transportation business
 */

import { supabase } from '../config/supabase';
import { transformToGSTR3B, generateReturnPeriod } from '../utils/gstTransformer';
import { batchValidateInvoices, validateFilingPeriod, validateGSTIN, checkFilingLateness } from '../utils/gstValidators';
import localforage from 'localforage';
import { toast } from 'react-toastify';

// Constants
const GST_FILING_HISTORY_KEY = 'gst_filing_history';
const GST_VALIDATION_CACHE_KEY = 'gst_validation_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Transportation SAC codes
const SAC_TRANSPORT_GOODS = '996791'; // 5% GST
const SAC_COURIER_SERVICES = '996511'; // 18% GST

/**
 * GST Service class for handling all GST-related operations via Whitebook APIs
 */
class GSTService {
  constructor() {
    this.supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    this.supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    this.whitebookBaseUrl = process.env.REACT_APP_WHITEBOOK_BASE_URL || 'https://apisandbox.whitebooks.in';
    
    // Validate environment variables
    if (!this.supabaseUrl) {
      console.error('REACT_APP_SUPABASE_URL is not set');
    }
    if (!this.supabaseAnonKey) {
      console.error('REACT_APP_SUPABASE_ANON_KEY is not set');
    }
    
    console.log('GST Service initialized with Whitebook APIs:', {
      supabaseUrl: this.supabaseUrl ? `${this.supabaseUrl.substring(0, 30)}...` : 'MISSING',
      whitebookBaseUrl: this.whitebookBaseUrl,
      hasAnonKey: !!this.supabaseAnonKey
    });
  }

  async getActiveSession(forceRefresh = false) {
    if (forceRefresh) {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data?.session) {
          console.log('Session refreshed successfully');
          return data.session;
        }
        console.warn('Session refresh failed:', error);
      } catch (error) {
        console.warn('Session refresh error:', error);
      }
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      return null;
    }

    // Check if session is about to expire (within 1 minute)
    const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
    if (expiresAtMs && expiresAtMs - 60_000 < Date.now()) {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data?.session) {
          return data.session;
        }
        console.warn('Auto session refresh failed:', error);
      } catch (error) {
        console.warn('Auto session refresh error:', error);
      }
    }

    return session;
  }

  /**
   * Validate and ensure we have a fresh session before making critical requests
   */
  async ensureFreshSession() {
    try {
      const session = await this.getActiveSession();
      if (!session) {
        throw new Error('No active session found. Please login.');
      }

      // Check if session expires within the next 5 minutes
      const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
      if (expiresAtMs && expiresAtMs - 300_000 < Date.now()) {
        console.log('Session expires soon, refreshing proactively');
        const refreshedSession = await this.getActiveSession(true);
        return refreshedSession || session;
      }

      // Validate that the session token is not corrupted
      if (!session.access_token || session.access_token.length < 50) {
        console.warn('Session token appears to be corrupted or too short');
        const refreshedSession = await this.getActiveSession(true);
        if (!refreshedSession?.access_token) {
          throw new Error('Unable to refresh session. Session may be corrupted.');
        }
        return refreshedSession;
      }

      // Additional validation - check if token looks like a JWT
      const tokenParts = session.access_token.split('.');
      if (tokenParts.length !== 3) {
        console.warn('Session token does not appear to be a valid JWT');
        const refreshedSession = await this.getActiveSession(true);
        if (!refreshedSession?.access_token) {
          throw new Error('Invalid session token format.');
        }
        return refreshedSession;
      }

      return session;
    } catch (error) {
      console.error('Session validation failed:', error);
      
      // Provide more specific error messaging
      if (error.message.includes('corrupted') || error.message.includes('refresh')) {
        throw new Error('Session validation failed. Please refresh the page and login again.');
      }
      
      throw error;
    }
  }

  async invokeEdgeFunction(functionName, payload, options = {}) {
    const { retry = true, forceRefresh = false, useSession = null } = options ?? {};
    let session = useSession || await this.getActiveSession(forceRefresh);

    try {
      if (!session) {
        throw new Error('No active session. Please login to continue.');
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      };

      // Always include the anon key for edge functions
      if (this.supabaseAnonKey) {
        headers['apikey'] = this.supabaseAnonKey;
      } else {
        console.warn('Missing REACT_APP_SUPABASE_ANON_KEY - edge function may fail');
      }

      // Debug: Log session info for troubleshooting
      console.log('Edge function request details:', {
        functionName,
        url: `${this.supabaseUrl}/functions/v1/${functionName}`,
        hasToken: !!session.access_token,
        tokenLength: session.access_token?.length,
        hasAnonKey: !!this.supabaseAnonKey,
        userId: session.user?.id,
        userEmail: session.user?.email,
        expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null
      });
      
      // Also log the headers being sent (without sensitive data)
      console.log('Request headers:', {
        'Content-Type': headers['Content-Type'],
        'Authorization': headers['Authorization'] ? 'Bearer [PRESENT]' : 'MISSING',
        'apikey': headers['apikey'] ? '[PRESENT]' : 'MISSING'
      });

      const response = await fetch(`${this.supabaseUrl}/functions/v1/${functionName}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload ?? {}),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error(`Edge function ${functionName} error:`, errorBody);

        if ((response.status === 401 || errorBody?.error?.includes?.('Unauthorized')) && response.status !== 422) {
          console.error('401 Unauthorized error details:', {
            status: response.status,
            statusText: response.statusText,
            errorBody,
            requestUrl: `${this.supabaseUrl}/functions/v1/${functionName}`,
            retryAttempt: !retry ? 'This was a retry' : 'First attempt'
          });
          
          if (retry) {
            try {
              console.log('Attempting to refresh session due to 401 error');
              
              // Force a new session refresh
              const { data, error } = await supabase.auth.refreshSession();
              if (!error && data?.session) {
                console.log('Session refreshed successfully, retrying request');
                console.log('New session details:', {
                  userId: data.session.user?.id,
                  email: data.session.user?.email,
                  tokenLength: data.session.access_token?.length,
                  expiresAt: data.session.expires_at ? new Date(data.session.expires_at * 1000).toISOString() : null
                });
                
                // Use the fresh session directly
                return this.invokeEdgeFunction(functionName, payload, { 
                  retry: false, 
                  forceRefresh: false,
                  useSession: data.session 
                });
              } else {
                console.error('Failed to refresh session:', error);
              }
            } catch (refreshError) {
              console.error('Session refresh attempt failed:', refreshError);
            }
          }

          // Only clear session if we've exhausted all retry attempts
          // Let the user try to refresh manually first
          throw new Error('Authentication failed. Please refresh and try again.');
        }

        // Handle GST credential errors specifically
        if (response.status === 422) {
          const err = new Error(errorBody?.error || 'Invalid GST portal credentials');
          err.status = response.status;
          err.details = errorBody;
          err.isCredentialError = true;
          throw err;
        }

        const err = new Error(errorBody?.error || `Request to ${functionName} failed (${response.status})`);
        err.status = response.status;
        err.details = errorBody;
        throw err;
      }

      const data = await response.json().catch(() => null);
      return data ?? { success: true };
    } catch (error) {
      console.error(`Failed to invoke ${functionName}:`, error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Request to ${functionName} failed`);
    }
  }

  /**
   * Get authentication token from UserService
   * @returns {Promise<string|null>} - Session token or null if not authenticated
   */
  async getAuthToken() {
    try {
      const userService = (await import('./userService')).default;
      return userService.sessionToken;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated using UserService
   * @returns {Promise<boolean>} - true if authenticated
   */
  async isAuthenticated() {
    const userService = (await import('./userService')).default;
    return userService.isAuthenticated();
  }

  /**
   * Test basic Supabase edge function connectivity with Whitebook APIs
   * @returns {Promise<Object>} Connection test result
   */
  async testEdgeFunctionConnectivity() {
    try {
      const session = await this.getActiveSession();
      if (!session) {
        return { success: false, error: 'No session available' };
      }

      // Test with a simple fetch to see what we get
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      };

      if (this.supabaseAnonKey) {
        headers['apikey'] = this.supabaseAnonKey;
      }

      console.log('Testing Whitebook edge function connectivity...');
      console.log('Environment check:', {
        supabaseUrl: this.supabaseUrl ? 'SET' : 'MISSING',
        anonKey: this.supabaseAnonKey ? 'SET' : 'MISSING',
        whitebookBaseUrl: this.whitebookBaseUrl,
        sessionExists: !!session,
        tokenExists: !!session.access_token
      });

      const testResponse = await fetch(`${this.supabaseUrl}/functions/v1/whitebook-status`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'get_session_info' })
      });

      const testResult = await testResponse.json().catch(() => ({}));
      
      return {
        success: testResponse.ok,
        status: testResponse.status,
        statusText: testResponse.statusText,
        headers: Object.fromEntries(testResponse.headers.entries()),
        body: testResult
      };
    } catch (error) {
      console.error('Edge function connectivity test failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check session health and return status
   * @returns {Promise<Object>} Session health status
   */
  async checkSessionHealth() {
    try {
      const session = await this.getActiveSession();
      if (!session) {
        return { healthy: false, reason: 'no_session', needsLogin: true };
      }

      if (!session.access_token || session.access_token.length < 50) {
        return { healthy: false, reason: 'corrupted_token', needsRefresh: true };
      }

      const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
      if (expiresAtMs && expiresAtMs - 60_000 < Date.now()) {
        return { healthy: false, reason: 'expiring_soon', needsRefresh: true };
      }

      // Test the session by making a simple authenticated request
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error || !data.user) {
          return { healthy: false, reason: 'invalid_session', needsRefresh: true };
        }
        
        // Also check if the user exists in our users table
        const { error: userError } = await supabase
          .from('users')
          .select('id')
          .eq('id', data.user.id)
          .single();
          
        if (userError && userError.code !== 'PGRST116') {
          console.warn('User record check failed:', userError);
          return { healthy: false, reason: 'user_record_missing', needsRefresh: false };
        }
      } catch (authError) {
        return { healthy: false, reason: 'auth_test_failed', needsRefresh: true };
      }

      return { healthy: true, expiresAt: expiresAtMs };
    } catch (error) {
      console.error('Session health check failed:', error);
      return { healthy: false, reason: 'check_failed', error: error.message };
    }
  }

  /**
   * Validate GSTIN in real-time using GSTZen
   * @param {string} gstin - GSTIN to validate
   * @param {boolean} useCache - Whether to use cached results
   * @returns {Promise<Object>} - Validation result
   */
  async validateGSTINOnline(gstin, useCache = true) {
    // First, do local format validation
    const localValidation = validateGSTIN(gstin);
    if (!localValidation.valid) {
      return {
        success: false,
        valid: false,
        error: localValidation.error
      };
    }

    // Check cache if enabled
    if (useCache) {
      const cached = await this.getValidationFromCache(gstin);
      if (cached) {
        return cached;
      }
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Session expired. Please login again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (this.supabaseAnonKey) {
        headers['apikey'] = this.supabaseAnonKey;
      }

      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/gstzen-validate`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ gstin })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Validation failed: ${response.statusText}`);
      }

      const result = await response.json();

      // Cache the result
      if (result.success && result.valid) {
        await this.saveValidationToCache(gstin, result);
      }

      return result;
    } catch (error) {
      console.error('GSTIN validation error:', error);
      return {
        success: false,
        valid: false,
        error: error.message || 'Failed to validate GSTIN online'
      };
    }
  }

  /**
   * File GSTR-1 return
   * @param {Array} invoices - Array of invoices to file
   * @param {string} companyGSTIN - Company GSTIN
   * @param {string} startDate - Start date of filing period
   * @param {string} endDate - End date of filing period
   * @returns {Promise<Object>} - Filing result with ARN
   */
  async fileGSTR1(invoices, companyGSTIN, startDate, endDate, filingOptions = {}) {
    try {
      // Validate company GSTIN
      const gstinValidation = validateGSTIN(companyGSTIN);
      if (!gstinValidation.valid) {
        throw new Error(`Invalid company GSTIN: ${gstinValidation.error}`);
      }

      // Generate return period
      const returnPeriod = generateReturnPeriod(startDate, endDate);

      // Validate filing period
      const periodValidation = validateFilingPeriod(returnPeriod);
      if (!periodValidation.valid) {
        throw new Error(`Invalid filing period: ${periodValidation.error}`);
      }

      // Check if filing is late
      const latenessCheck = checkFilingLateness(returnPeriod, 'GSTR1');
      if (latenessCheck.isLate) {
        toast.warning(
          `Filing is ${latenessCheck.daysLate} days late. Due date was ${latenessCheck.dueDate.toLocaleDateString()}`,
          { autoClose: 5000 }
        );
      }

      // Validate all invoices
      const validation = batchValidateInvoices(invoices);
      if (!validation.valid) {
        const errorMessages = validation.invalidInvoices
          .map(inv => `Invoice ${inv.invoice.invoiceNo}: ${inv.errors.join(', ')}`)
          .join('\n');
        throw new Error(`Invoice validation failed:\n${errorMessages}`);
      }

      // Transform invoices to Whitebook GSTR-1 format
      const gstr1Data = await this.transformToWhitebookGSTR1Format(
        validation.validInvoices, 
        companyGSTIN, 
        returnPeriod
      );

      // Validate required parameters
      if (!filingOptions.evcOtp) {
        throw new Error('EVC OTP is required for GST filing');
      }

      // First save the data
      console.log('Saving GSTR-1 data via Whitebook API...');
      const saveResult = await this.invokeEdgeFunction('whitebook-gstr1', {
        action: 'save_data',
        data: gstr1Data
      });

      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Failed to save GSTR-1 data');
      }

      // Get summary for validation
      console.log('Getting GSTR-1 summary...');
      const summaryResult = await this.invokeEdgeFunction('whitebook-gstr1', {
        action: 'get_summary',
        gstin: companyGSTIN,
        retPeriod: returnPeriod
      });

      // File the return
      console.log('Filing GSTR-1 return via Whitebook API...');
      const result = await this.invokeEdgeFunction('whitebook-gstr1', {
        action: 'file_return',
        gstin: companyGSTIN,
        retPeriod: returnPeriod,
        otp: filingOptions.evcOtp,
        pan: filingOptions.authorizedSignatoryPan || filingOptions.pan
      });

      // Add session info to result
      if (result.success) {
        result.platform = 'whitebook';
        result.summary = summaryResult;
      }

      // Save filing record to history
      if (result.success) {
        await this.saveFilingHistory({
          type: 'GSTR1',
          returnPeriod,
          filedAt: new Date().toISOString(),
          invoiceCount: invoices.length,
          arn: result.arn,
          refId: result.refId,
          status: 'filed',
          platform: 'whitebook',
          data: gstr1Data
        });
      }

      return result;
    } catch (error) {
      console.error('GSTR-1 filing error:', error);
      throw error;
    }
  }

  /**
   * Determine service type based on invoice details for transportation business
   * @param {Object} invoice - Invoice object
   * @returns {string} - Service type
   */
  determineServiceType(invoice) {
    // Check description or other fields to determine service type
    const description = (invoice.description || '').toLowerCase();
    const itemName = (invoice.items?.[0]?.itemName || '').toLowerCase();
    
    if (description.includes('courier') || itemName.includes('courier') || 
        description.includes('express') || itemName.includes('express')) {
      return 'courier_services'; // SAC 996511 - 18% GST
    }
    
    // Default to goods transport
    return 'goods_transport'; // SAC 996791 - 5% GST
  }

  /**
   * File GSTR-3B return
   * @param {Array} invoices - Array of invoices for the period
   * @param {string} companyGSTIN - Company GSTIN
   * @param {string} startDate - Start date of filing period
   * @param {string} endDate - End date of filing period
   * @returns {Promise<Object>} - Filing result
   */
  async fileGSTR3B(invoices, companyGSTIN, startDate, endDate, filingOptions = {}) {
    try {
      // Validate company GSTIN
      const gstinValidation = validateGSTIN(companyGSTIN);
      if (!gstinValidation.valid) {
        throw new Error(`Invalid company GSTIN: ${gstinValidation.error}`);
      }

      // Generate return period
      const returnPeriod = generateReturnPeriod(startDate, endDate);

      // Validate filing period
      const periodValidation = validateFilingPeriod(returnPeriod);
      if (!periodValidation.valid) {
        throw new Error(`Invalid filing period: ${periodValidation.error}`);
      }

      // Check if filing is late
      const latenessCheck = checkFilingLateness(returnPeriod, 'GSTR3B');
      if (latenessCheck.isLate) {
        toast.warning(
          `Filing is ${latenessCheck.daysLate} days late. Due date was ${latenessCheck.dueDate.toLocaleDateString()}`,
          { autoClose: 5000 }
        );
      }

      // Validate all invoices
      const validation = batchValidateInvoices(invoices);
      if (!validation.valid) {
        const errorMessages = validation.invalidInvoices
          .map(inv => `Invoice ${inv.invoice.invoiceNo}: ${inv.errors.join(', ')}`)
          .join('\n');
        throw new Error(`Invoice validation failed:\n${errorMessages}`);
      }

      // Transform invoices to GSTR-3B format optimized for transportation business
      const gstr3bData = await this.transformToWhitebookGSTR3BFormat(
        validation.validInvoices,
        companyGSTIN,
        returnPeriod
      );

      // Validate required parameters
      if (!filingOptions.evcOtp) {
        throw new Error('EVC OTP is required for GST filing');
      }

      // First save the GSTR-3B data
      console.log('Saving GSTR-3B data via Whitebook API...');
      const saveResult = await this.invokeEdgeFunction('whitebook-gstr3b', {
        action: 'save_data',
        data: gstr3bData
      });

      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Failed to save GSTR-3B data');
      }

      // File the return
      console.log('Filing GSTR-3B return via Whitebook API...');
      const result = await this.invokeEdgeFunction('whitebook-gstr3b', {
        action: 'file_return',
        gstin: companyGSTIN,
        retPeriod: returnPeriod,
        otp: filingOptions.evcOtp,
        pan: filingOptions.authorizedSignatoryPan || filingOptions.pan
      });

      // Add platform info to result
      if (result.success) {
        result.platform = 'whitebook';
        result.returnType = 'GSTR3B';
      }

      // Save filing record to history
      if (result.success) {
        await this.saveFilingHistory({
          type: 'GSTR3B',
          returnPeriod,
          filedAt: new Date().toISOString(),
          invoiceCount: invoices.length,
          arn: result.arn,
          refId: result.refId,
          status: 'filed',
          platform: 'whitebook',
          data: gstr3bData
        });
      }

      return result;
    } catch (error) {
      console.error('GSTR-3B filing error:', error);
      throw error;
    }
  }

  /**
   * Get filing history
   * @returns {Promise<Array>} - Array of filing records
   */
  async getFilingHistory() {
    try {
      const history = await localforage.getItem(GST_FILING_HISTORY_KEY);
      return history || [];
    } catch (error) {
      console.error('Error loading filing history:', error);
      return [];
    }
  }

  /**
   * Save filing record to history
   * @param {Object} record - Filing record
   */
  async saveFilingHistory(record) {
    try {
      const history = await this.getFilingHistory();
      history.unshift(record); // Add to beginning

      // Keep only last 100 records
      const trimmedHistory = history.slice(0, 100);

      await localforage.setItem(GST_FILING_HISTORY_KEY, trimmedHistory);
    } catch (error) {
      console.error('Error saving filing history:', error);
    }
  }

  /**
   * Get validation result from cache
   * @param {string} gstin - GSTIN
   * @returns {Promise<Object|null>} - Cached validation result or null
   */
  async getValidationFromCache(gstin) {
    try {
      const cache = await localforage.getItem(GST_VALIDATION_CACHE_KEY) || {};
      const cached = cache[gstin];

      if (cached && cached.timestamp) {
        const age = Date.now() - cached.timestamp;
        if (age < CACHE_DURATION) {
          return cached.result;
        }
      }

      return null;
    } catch (error) {
      console.error('Error reading validation cache:', error);
      return null;
    }
  }

  /**
   * Save validation result to cache
   * @param {string} gstin - GSTIN
   * @param {Object} result - Validation result
   */
  async saveValidationToCache(gstin, result) {
    try {
      const cache = await localforage.getItem(GST_VALIDATION_CACHE_KEY) || {};
      cache[gstin] = {
        timestamp: Date.now(),
        result
      };

      await localforage.setItem(GST_VALIDATION_CACHE_KEY, cache);
    } catch (error) {
      console.error('Error saving validation cache:', error);
    }
  }

  /**
   * Clear validation cache
   */
  async clearValidationCache() {
    try {
      await localforage.removeItem(GST_VALIDATION_CACHE_KEY);
    } catch (error) {
      console.error('Error clearing validation cache:', error);
    }
  }

  /**
   * Get filing status for a specific period
   * @param {string} returnPeriod - Return period in MMYYYY format
   * @param {string} returnType - 'GSTR1' or 'GSTR3B'
   * @returns {Promise<Object|null>} - Filing record or null
   */
  async getFilingStatus(returnPeriod, returnType) {
    try {
      const history = await this.getFilingHistory();
      return history.find(
        record => record.returnPeriod === returnPeriod && record.type === returnType
      ) || null;
    } catch (error) {
      console.error('Error getting filing status:', error);
      return null;
    }
  }

  /**
   * Check if return has been filed for a period
   * @param {string} returnPeriod - Return period in MMYYYY format
   * @param {string} returnType - 'GSTR1' or 'GSTR3B'
   * @returns {Promise<boolean>} - true if filed
   */
  async isReturnFiled(returnPeriod, returnType) {
    const status = await this.getFilingStatus(returnPeriod, returnType);
    return status !== null && status.status === 'filed';
  }

  /**
   * Enhanced session management with automatic retry and validation
   */
  async requestSessionOtp(gstin, gstnUsername) {
    if (!await this.isAuthenticated()) {
      throw new Error('Please login to continue with GST filing');
    }

    if (!gstin || !gstnUsername) {
      throw new Error('GSTIN and username are required for OTP request');
    }

    try {
      const result = await this.invokeEdgeFunction('gstzen-gstn-otp', {
        gstin: gstin.toUpperCase().trim(),
        gstnUsername: gstnUsername.trim()
      });

      if (result?.success) {
        // Cache the OTP request time to prevent spam
        localStorage.setItem(`gst_otp_requested_${gstin}`, Date.now().toString());
        return result;
      } else {
        throw new Error(result?.error || 'Failed to request OTP');
      }
    } catch (error) {
      console.error('OTP request failed:', error);
      throw error;
    }
  }

  async establishSession(gstin, otp) {
    if (!await this.isAuthenticated()) {
      throw new Error('Please login to continue with GST filing');
    }

    if (!gstin || !otp || otp.length !== 6) {
      throw new Error('Valid GSTIN and 6-digit OTP are required');
    }

    try {
      const result = await this.invokeEdgeFunction('gstzen-gstn-session', {
        gstin: gstin.toUpperCase().trim(),
        otp: otp.trim()
      });

      if (result?.success) {
        // Clear OTP request cache after successful session establishment
        localStorage.removeItem(`gst_otp_requested_${gstin}`);
        // Cache session status for quick access
        localStorage.setItem(`gst_session_${gstin}`, JSON.stringify({
          hasSession: true,
          expiresAt: result.data?.expiresAt,
          establishedAt: Date.now()
        }));
        return result;
      } else {
        throw new Error(result?.error || 'Failed to establish session');
      }
    } catch (error) {
      console.error('Session establishment failed:', error);
      throw error;
    }
  }

  async getSessionStatus(gstin, useCache = true) {
    if (!await this.isAuthenticated()) {
      throw new Error('Please login to continue with GST filing');
    }

    if (!gstin) {
      throw new Error('GSTIN is required for session status check');
    }

    // Check cache first if enabled
    if (useCache) {
      const cached = localStorage.getItem(`gst_session_${gstin}`);
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          const expiresAt = new Date(cachedData.expiresAt);
          const now = new Date();

          // If session hasn't expired yet, return cached data
          if (expiresAt > now) {
            const minutesRemaining = Math.ceil((expiresAt - now) / (1000 * 60));
            return {
              success: true,
              hasSession: true,
              session: {
                expiresAt: cachedData.expiresAt,
                minutesRemaining: minutesRemaining
              }
            };
          } else {
            // Clear expired session from cache
            localStorage.removeItem(`gst_session_${gstin}`);
          }
        } catch (e) {
          console.warn('Invalid cached session data:', e);
          localStorage.removeItem(`gst_session_${gstin}`);
        }
      }
    }

    try {
      const result = await this.invokeEdgeFunction('gstzen-session-status', {
        gstin: gstin.toUpperCase().trim()
      });

      // Update cache with fresh data
      if (result?.success && result.hasSession) {
        localStorage.setItem(`gst_session_${gstin}`, JSON.stringify({
          hasSession: true,
          expiresAt: result.session?.expiresAt,
          establishedAt: Date.now()
        }));
      } else {
        localStorage.removeItem(`gst_session_${gstin}`);
      }

      return result;
    } catch (error) {
      console.error('Session status check failed:', error);
      throw error;
    }
  }

  /**
   * Automated session management - handles session creation/renewal internally
   * Returns session info or creates new session if needed
   */
  async autoManageSession(gstin, evcOtp) {
    try {
      // Try to use existing session first
      const status = await this.getSessionStatus(gstin, true);

      if (status?.success && status.hasSession) {
        const minutesRemaining = status.session?.minutesRemaining || 0;

        if (minutesRemaining > 2) {
          return {
            success: true,
            sessionReady: true,
            minutesRemaining,
            rateHint: 'reused_session'
          };
        }
      }

      // If no valid session, the filing endpoint will handle session creation automatically
      return {
        success: true,
        sessionReady: false,
        message: 'Will create new session during filing',
        rateHint: 'new_session_auto'
      };
    } catch (error) {
      console.warn('Session check failed, will attempt auto-creation:', error);
      return {
        success: true,
        sessionReady: false,
        message: 'Will attempt session auto-creation',
        rateHint: 'fallback_mode'
      };
    }
  }

  /**
   * Check if OTP was recently requested to prevent spam
   */
  canRequestOtp(gstin) {
    const lastRequest = localStorage.getItem(`gst_otp_requested_${gstin}`);
    if (!lastRequest) return true;

    const timeDiff = Date.now() - parseInt(lastRequest);
    const minutesSince = Math.floor(timeDiff / (1000 * 60));

    // Allow new OTP request after 2 minutes
    return minutesSince >= 2;
  }

  /**
   * Request OTP for Whitebook authentication
   * @param {string} email - Email address
   * @returns {Promise<Object>} - OTP request result with txn
   */
  async requestAuthOtp(email) {
    if (!email) {
      throw new Error('Email is required for OTP request');
    }

    try {
      const result = await this.invokeEdgeFunction('whitebook-auth', {
        action: 'request_otp',
        email: email.toLowerCase().trim()
      });

      if (result?.success) {
        return {
          success: true,
          txn: result.txn,
          message: 'OTP sent to email successfully'
        };
      } else {
        throw new Error(result?.error || 'Failed to request OTP');
      }
    } catch (error) {
      console.error('Auth OTP request failed:', error);
      throw error;
    }
  }

  /**
   * Verify OTP and get auth token
   * @param {string} email - Email address
   * @param {string} otp - OTP from email
   * @param {string} txn - Transaction ID from OTP request
   * @returns {Promise<Object>} - Auth result with token
   */
  async verifyAuthOtp(email, otp, txn) {
    if (!email || !otp) {
      throw new Error('Email and OTP are required');
    }

    try {
      const result = await this.invokeEdgeFunction('whitebook-auth', {
        action: 'verify_otp',
        email: email.toLowerCase().trim(),
        otp: otp.toString(),
        txn: txn
      });

      if (result?.success) {
        return {
          success: true,
          authToken: result.authToken,
          expiryTime: result.expiryTime,
          message: 'Authentication successful'
        };
      } else {
        throw new Error(result?.error || 'OTP verification failed');
      }
    } catch (error) {
      console.error('Auth OTP verification failed:', error);
      throw error;
    }
  }

  /**
   * Request EVC OTP for filing (Whitebook API)
   * @param {string} email - Email address
   * @param {string} gstPassword - GST portal password
   * @returns {Promise<Object>} - EVC OTP request result
   */
  async requestEvcOtp(email, gstPassword) {
    if (!email || !gstPassword) {
      throw new Error('Email and GST password are required for EVC OTP');
    }

    try {
      const result = await this.invokeEdgeFunction('whitebook-auth', {
        action: 'request_evc_otp',
        email: email.toLowerCase().trim(),
        gstPassword: gstPassword
      });

      if (result?.success) {
        return {
          success: true,
          message: 'EVC OTP sent to registered mobile'
        };
      } else {
        throw new Error(result?.error || 'Failed to request EVC OTP');
      }
    } catch (error) {
      console.error('EVC OTP request failed:', error);
      throw error;
    }
  }

  /**
   * Request EVC OTP using GST portal credentials (Legacy method - keeping for compatibility)
   * @param {string} gstin - GSTIN for which to request EVC OTP
   * @param {string} gstUsername - GST portal username
   * @param {string} gstPassword - GST portal password
   * @returns {Promise<Object>} - Request result
   */
  async requestEvcOtpLegacy(gstin, gstUsername, gstPassword) {
    // Check session health comprehensively before making the request
    const sessionHealth = await this.checkSessionHealth();
    if (!sessionHealth.healthy) {
      console.warn('Session health check failed:', sessionHealth);
      
      if (sessionHealth.needsLogin) {
        throw new Error('Please login to continue with GST services.');
      } else if (sessionHealth.needsRefresh) {
        try {
          await this.ensureFreshSession();
        } catch (sessionError) {
          throw new Error('Session validation failed. Please refresh the page and login again.');
        }
      }
    }

    if (!gstin) {
      throw new Error('GSTIN is required for EVC OTP request');
    }

    if (!gstUsername || !gstPassword) {
      throw new Error('GST portal username and password are required');
    }

    // Validate GSTIN format before making request
    const gstinValidation = validateGSTIN(gstin);
    if (!gstinValidation.valid) {
      throw new Error(`Invalid GSTIN: ${gstinValidation.error}`);
    }

    // Check if recent EVC request was made to prevent spam
    const lastEvcRequest = localStorage.getItem(`gst_evc_requested_${gstin}`);
    if (lastEvcRequest) {
      const timeDiff = Date.now() - parseInt(lastEvcRequest);
      const minutesSince = Math.floor(timeDiff / (1000 * 60));

      if (minutesSince < 3) { // Wait 3 minutes between EVC requests
        throw new Error(`Please wait ${3 - minutesSince} more minute(s) before requesting another EVC OTP`);
      }
    }

    try {
      const result = await this.invokeEdgeFunction('whitebook-auth', {
        action: 'request_evc_otp',
        gstin: gstin.toUpperCase().trim(),
        gstPassword: gstPassword // Password sent securely to edge function
      });

      if (result?.success) {
        // Cache the EVC request time to prevent spam
        localStorage.setItem(`whitebook_evc_requested_${gstin}`, Date.now().toString());
        
        // Store EVC transaction ID
        localStorage.setItem(`whitebook_evc_txn_${gstin}`, result.txn);

        // Provide user guidance based on result
        return {
          ...result,
          guidance: {
            message: 'EVC OTP sent using your GST portal credentials via Whitebook API',
            steps: [
              'Check your SMS for 6-digit EVC OTP',
              'Enter the OTP in the field below',
              'OTP is valid for 10 minutes',
              'Your credentials are not stored anywhere',
              'Default sandbox OTP: 575757'
            ]
          }
        };
      } else {
        throw new Error(result?.error || 'Failed to request EVC OTP');
      }
    } catch (error) {
      console.error('EVC OTP request failed:', error);

      // Enhanced error handling with user-friendly messages
      if (error.message.includes('credentials') || error.message.includes('authentication')) {
        throw new Error('Invalid GST portal credentials. Please check your username and password.');
      } else if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
        throw new Error('Too many requests. Please wait 5 minutes before trying again.');
      } else if (error.message.includes('invalid') || error.message.includes('not registered')) {
        throw new Error('GSTIN not registered for GST filing or invalid. Please verify your GSTIN.');
      } else if (error.message.includes('network') || error.message.includes('timeout')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }

      throw error;
    }
  }

  /**
   * Validate GST portal credentials format
   * @param {string} username - GST portal username
   * @param {string} password - GST portal password
   * @returns {Object} - Validation result
   */
  validateGstCredentials(username, password) {
    if (!username || username.trim().length === 0) {
      return {
        valid: false,
        error: 'GST portal username is required'
      };
    }

    if (!password || password.length === 0) {
      return {
        valid: false,
        error: 'GST portal password is required'
      };
    }

    if (username.trim().length < 3) {
      return {
        valid: false,
        error: 'GST username must be at least 3 characters'
      };
    }

    if (password.length < 6) {
      return {
        valid: false,
        error: 'GST password must be at least 6 characters'
      };
    }

    return {
      valid: true,
      message: 'Credentials format valid'
    };
  }

  /**
   * Check if EVC OTP can be requested (rate limiting)
   * @param {string} gstin - GSTIN to check
   * @returns {Object} - Rate limit status
   */
  canRequestEvcOtp(gstin) {
    const lastRequest = localStorage.getItem(`gst_evc_requested_${gstin}`);
    if (!lastRequest) {
      return { canRequest: true, waitTime: 0 };
    }

    const timeDiff = Date.now() - parseInt(lastRequest);
    const minutesSince = Math.floor(timeDiff / (1000 * 60));
    const waitTime = Math.max(3 - minutesSince, 0);

    return {
      canRequest: waitTime === 0,
      waitTime: waitTime,
      message: waitTime > 0 ? `Please wait ${waitTime} more minute(s)` : 'Ready to request'
    };
  }

  /**
   * Validate EVC OTP format and provide guidance
   * @param {string} evcOtp - EVC OTP to validate
   * @returns {Object} - Validation result with guidance
   */
  validateEvcOtp(evcOtp) {
    if (!evcOtp) {
      return {
        valid: false,
        error: 'EVC OTP is required',
        guidance: 'Please enter the 6-digit OTP received on your registered mobile number'
      };
    }

    const cleanOtp = evcOtp.toString().trim();

    if (cleanOtp.length !== 6) {
      return {
        valid: false,
        error: 'EVC OTP must be exactly 6 digits',
        guidance: 'Please check your SMS and enter all 6 digits'
      };
    }

    if (!/^\d{6}$/.test(cleanOtp)) {
      return {
        valid: false,
        error: 'EVC OTP must contain only numbers',
        guidance: 'Please enter only the numeric digits from your SMS'
      };
    }

    return {
      valid: true,
      cleanOtp: cleanOtp,
      message: 'Valid EVC OTP format'
    };
  }

  /**
   * Enhanced filing workflow with automated EVC and session management
   * @param {string} filingType - 'GSTR1' or 'GSTR3B'
   * @param {Array} invoices - Invoice data
   * @param {string} companyGSTIN - Company GSTIN
   * @param {string} startDate - Filing period start
   * @param {string} endDate - Filing period end
   * @param {string} evcOtp - EVC OTP from user
   * @returns {Promise<Object>} - Filing result with detailed status
   */
  async automatedFiling(filingType, invoices, companyGSTIN, startDate, endDate, evcOtp) {
    // Step 1: Validate inputs
    const evcValidation = this.validateEvcOtp(evcOtp);
    if (!evcValidation.valid) {
      throw new Error(evcValidation.error);
    }

    const gstinValidation = validateGSTIN(companyGSTIN);
    if (!gstinValidation.valid) {
      throw new Error(`Invalid company GSTIN: ${gstinValidation.error}`);
    }

    if (!invoices || invoices.length === 0) {
      throw new Error('No invoices selected for filing');
    }

    // Step 2: Pre-filing preparation
    const filingOptions = {
      evcOtp: evcValidation.cleanOtp,
      autoSession: true,
      enhancedValidation: true
    };

    // Step 3: Execute filing based on type
    let result;
    try {
      if (filingType === 'GSTR1') {
        result = await this.fileGSTR1(invoices, companyGSTIN, startDate, endDate, filingOptions);
      } else if (filingType === 'GSTR3B') {
        result = await this.fileGSTR3B(invoices, companyGSTIN, startDate, endDate, filingOptions);
      } else {
        throw new Error('Invalid filing type. Must be GSTR1 or GSTR3B');
      }

      // Step 4: Post-filing cleanup and status
      if (result.success) {
        // Clear EVC request cache after successful filing
        localStorage.removeItem(`gst_evc_requested_${companyGSTIN}`);

        return {
          ...result,
          filingType,
          invoiceCount: invoices.length,
          automation: {
            evcUsed: true,
            sessionManaged: true,
            validationPassed: true
          }
        };
      }

      return result;
    } catch (error) {
      console.error(`${filingType} automated filing error:`, error);

      // Enhanced error context for better debugging
      throw new Error(`${filingType} Filing Failed: ${error.message}`);
    }
  }

  /**
   * Clear all cached session data for a GSTIN
   */
  clearSessionCache(gstin) {
    localStorage.removeItem(`gst_session_${gstin}`);
    localStorage.removeItem(`gst_otp_requested_${gstin}`);
    localStorage.removeItem(`gst_evc_requested_${gstin}`);
  }

  /**
   * Get comprehensive filing status for dashboard
   * @param {string} gstin - GSTIN to check
   * @returns {Promise<Object>} - Complete status overview
   */
  async getFilingDashboard(gstin) {
    try {
      const sessionStatus = await this.getSessionStatus(gstin, true);
      const evcRateLimit = this.canRequestEvcOtp(gstin);
      const filingHistory = await this.getFilingHistory();

      const recentFilings = filingHistory
        .filter(filing => filing.data?.companyGSTIN === gstin)
        .slice(0, 5); // Last 5 filings

      return {
        gstin,
        session: sessionStatus,
        evcStatus: evcRateLimit,
        recentFilings,
        systemReady: sessionStatus?.success && evcRateLimit?.canRequest,
        lastUpdate: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting filing dashboard:', error);
      return {
        gstin,
        error: error.message,
        systemReady: false,
        lastUpdate: new Date().toISOString()
      };
    }
  }

  // Annual Returns Methods
  
  /**
   * Save GSTR-9 annual return data
   */
  async saveGSTR9(gstin, financialYear, annualData) {
    console.log(`Saving GSTR-9 for GSTIN: ${gstin}, FY: ${financialYear}`);
    
    try {
      const session = await this.ensureFreshSession();
      if (!session) throw new Error('Authentication session not found');

      const { data, error } = await supabase.functions.invoke('whitebook-annual', {
        body: {
          action: 'save_gstr9',
          gstin,
          retPeriod: financialYear,
          annualData
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('GSTR-9 save error:', error);
        throw new Error(`Failed to save GSTR-9: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'GSTR-9 save failed');
      }

      console.log('GSTR-9 saved successfully:', data);
      toast.success('GSTR-9 annual return saved successfully');
      
      return data;
    } catch (error) {
      console.error('Error saving GSTR-9:', error);
      toast.error(`Failed to save GSTR-9: ${error.message}`);
      throw error;
    }
  }

  /**
   * File GSTR-9 Annual Return via Whitebook API
   * @param {string} gstin - GSTIN 
   * @param {string} financialYear - Financial year (e.g., "2024")
   * @param {string} otp - EVC OTP for filing
   * @param {string} pan - PAN of authorized signatory
   */
  async fileGSTR9(gstin, financialYear, otp, pan) {
    try {
      if (!gstin || !financialYear || !otp || !pan) {
        throw new Error('GSTIN, financial year, OTP, and PAN are required');
      }

      const result = await this.invokeEdgeFunction('whitebook-annual', {
        action: 'file_gstr9',
        gstin: gstin,
        ret_period: financialYear,
        otp: otp,
        pan: pan
      });

      if (!result.success) {
        throw new Error(result.error || 'GSTR-9 filing failed');
      }

      console.log('GSTR-9 filed successfully:', result);
      toast.success(`GSTR-9 filed successfully. ARN: ${result.arn}`);
      
      return result;
    } catch (error) {
      console.error('Error filing GSTR-9:', error);
      toast.error(`Failed to file GSTR-9: ${error.message}`);
      throw error;
    }
  }

  /**
   * Save GSTR-9C annual return with audit data
   */
  async saveGSTR9C(gstin, financialYear, auditData) {
    console.log(`Saving GSTR-9C for GSTIN: ${gstin}, FY: ${financialYear}`);
    
    try {
      const session = await this.ensureFreshSession();
      if (!session) throw new Error('Authentication session not found');

      const { data, error } = await supabase.functions.invoke('whitebook-annual', {
        body: {
          action: 'save_gstr9c',
          gstin,
          retPeriod: financialYear,
          auditData
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('GSTR-9C save error:', error);
        throw new Error(`Failed to save GSTR-9C: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'GSTR-9C save failed');
      }

      console.log('GSTR-9C saved successfully:', data);
      toast.success('GSTR-9C annual return with audit saved successfully');
      
      return data;
    } catch (error) {
      console.error('Error saving GSTR-9C:', error);
      toast.error(`Failed to save GSTR-9C: ${error.message}`);
      throw error;
    }
  }

  /**
   * File GSTR-9C Annual Return with Audit via Whitebook API
   * @param {string} gstin - GSTIN 
   * @param {string} financialYear - Financial year (e.g., "2024")
   * @param {string} otp - EVC OTP for filing
   * @param {string} pan - PAN of authorized signatory
   */
  async fileGSTR9C(gstin, financialYear, otp, pan) {
    console.log(`Filing GSTR-9C for GSTIN: ${gstin}, FY: ${financialYear}`);
    
    try {
      if (!gstin || !financialYear || !otp || !pan) {
        throw new Error('GSTIN, financial year, OTP, and PAN are required');
      }

      const result = await this.invokeEdgeFunction('whitebook-annual', {
        action: 'file_gstr9c',
        gstin: gstin,
        ret_period: financialYear,
        otp: otp,
        pan: pan
      });

      if (!result.success) {
        throw new Error(result.error || 'GSTR-9C filing failed');
      }

      console.log('GSTR-9C filed successfully:', result);
      toast.success(`GSTR-9C filed successfully. ARN: ${result.arn}`);
      
      return result;
    } catch (error) {
      console.error('Error filing GSTR-9C:', error);
      toast.error(`Failed to file GSTR-9C: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get annual return filing status
   */
  async getAnnualReturnStatus(gstin, financialYear, refId, returnType) {
    console.log(`Getting ${returnType} status for GSTIN: ${gstin}, FY: ${financialYear}`);
    
    try {
      const session = await this.ensureFreshSession();
      if (!session) throw new Error('Authentication session not found');

      const { data, error } = await supabase.functions.invoke('whitebook-annual', {
        body: {
          action: 'get_annual_status',
          gstin,
          retPeriod: financialYear,
          refId,
          returnType
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Annual return status error:', error);
        throw new Error(`Failed to get ${returnType} status: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || `${returnType} status check failed`);
      }

      console.log(`${returnType} status retrieved:`, data);
      
      return data;
    } catch (error) {
      console.error(`Error getting ${returnType} status:`, error);
      toast.error(`Failed to get ${returnType} status: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get available financial years for annual returns
   */
  getAvailableFinancialYears() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    
    // Financial year starts from April (month 3 in 0-indexed)
    let startYear = currentYear;
    if (currentMonth < 3) {
      startYear = currentYear - 1;
    }
    
    const financialYears = [];
    
    // Add previous 5 financial years
    for (let i = 0; i < 5; i++) {
      const fy = startYear - i;
      financialYears.push({
        value: fy.toString(),
        label: `FY ${fy}-${(fy + 1).toString().slice(2)}`,
        period: `${fy}`,
        displayName: `${fy}-${(fy + 1).toString().slice(2)}`
      });
    }
    
    return financialYears;
  }

  /**
   * Calculate transportation business annual summary
   */
  calculateAnnualSummary(invoices, financialYear) {
    const fyStart = new Date(`${financialYear}-04-01`);
    const fyEnd = new Date(`${parseInt(financialYear) + 1}-03-31`);
    
    const annualInvoices = invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.date);
      return invoiceDate >= fyStart && invoiceDate <= fyEnd;
    });
    
    let totalTurnover = 0;
    let transportTurnover = 0;
    let courierTurnover = 0;
    let totalTaxPaid = 0;
    let totalITCAvailed = 0;
    
    annualInvoices.forEach(invoice => {
      const amount = parseFloat(invoice.total || 0);
      const taxAmount = parseFloat(invoice.tax_amount || 0);
      const itcAmount = parseFloat(invoice.itc_amount || 0);
      
      totalTurnover += amount;
      totalTaxPaid += taxAmount;
      totalITCAvailed += itcAmount;
      
      // Classify by service type based on products/services
      const hasTransport = invoice.products?.some(p => 
        p.sacCode === SAC_TRANSPORT_GOODS || 
        p.description?.toLowerCase().includes('transport') ||
        p.description?.toLowerCase().includes('freight')
      );
      
      const hasCourier = invoice.products?.some(p => 
        p.sacCode === SAC_COURIER_SERVICES ||
        p.description?.toLowerCase().includes('courier') ||
        p.description?.toLowerCase().includes('delivery')
      );
      
      if (hasTransport) {
        transportTurnover += amount;
      } else if (hasCourier) {
        courierTurnover += amount;
      } else {
        // Default to transport for other logistics services
        transportTurnover += amount;
      }
    });
    
    return {
      financialYear,
      totalInvoices: annualInvoices.length,
      totalTurnover,
      transportTurnover,
      courierTurnover,
      totalTaxPaid,
      totalITCAvailed,
      avgInvoiceValue: annualInvoices.length > 0 ? totalTurnover / annualInvoices.length : 0,
      requiresAudit: totalTurnover > 20000000, // 2 Crores threshold for GSTR-9C
      filingDeadline: new Date(`${parseInt(financialYear) + 1}-12-31`), // Dec 31 of next FY
      auditDeadline: totalTurnover > 20000000 ? new Date(`${parseInt(financialYear) + 2}-01-31`) : null // Jan 31 for audit
    };
  }

  /**
   * Transform invoices to Whitebook GSTR-1 API format
   * @param {Array} invoices - Array of validated invoices
   * @param {string} gstin - Company GSTIN
   * @param {string} retPeriod - Return period
   * @returns {Object} - Whitebook GSTR-1 formatted data
   */
  async transformToWhitebookGSTR1Format(invoices, gstin, retPeriod) {
    const b2bInvoices = [];

    // Group invoices by client GSTIN for B2B
    const b2bMap = new Map();
    // Collect B2CS invoices (unregistered customers)
    const b2csMap = new Map();
    // Collect HSN/SAC summary
    const hsnMap = new Map();

    for (const invoice of invoices) {
      const serviceType = this.determineServiceType(invoice);
      const sacCode = serviceType === 'transport' ? SAC_TRANSPORT_GOODS : SAC_COURIER_SERVICES;
      const gstRate = serviceType === 'transport' ? 5 : 18;
      
      const taxableValue = parseFloat(invoice.taxableAmount || 0);
      const igst = parseFloat(invoice.igst || 0);
      const cgst = parseFloat(invoice.cgst || 0);
      const sgst = parseFloat(invoice.sgst || 0);
      const totalValue = parseFloat(invoice.total || 0);

      if (invoice.clientGSTIN && invoice.clientGSTIN.trim()) {
        // B2B Invoice
        const clientGstin = invoice.clientGSTIN.trim().toUpperCase();
        if (!b2bMap.has(clientGstin)) {
          b2bMap.set(clientGstin, []);
        }
        
        b2bMap.get(clientGstin).push({
          inum: invoice.invoiceNo,
          idt: new Date(invoice.invoiceDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          val: totalValue,
          pos: invoice.placeOfSupply || '01',
          rchrg: 'N',
          inv_typ: 'R',
          diff_percent: 0,
          itms: [{
            num: 1,
            itm_det: {
              rt: gstRate,
              txval: taxableValue,
              iamt: igst,
              camt: cgst,
              samt: sgst,
              csamt: 0
            }
          }]
        });
      } else {
        // B2CS Invoice (unregistered customer)
        const key = `${invoice.placeOfSupply || '01'}_${gstRate}`;
        if (!b2csMap.has(key)) {
          b2csMap.set(key, {
            sply_ty: invoice.placeOfSupply === gstin.substring(0, 2) ? 'INTRA' : 'INTER',
            pos: invoice.placeOfSupply || '01',
            typ: 'OE',
            rt: gstRate,
            txval: 0,
            iamt: 0,
            camt: 0,
            samt: 0,
            csamt: 0
          });
        }
        
        const b2csEntry = b2csMap.get(key);
        b2csEntry.txval += taxableValue;
        b2csEntry.iamt += igst;
        b2csEntry.camt += cgst;
        b2csEntry.samt += sgst;
      }

      // HSN/SAC Summary
      if (!hsnMap.has(sacCode)) {
        hsnMap.set(sacCode, {
          num: hsnMap.size + 1,
          hsn_sc: sacCode,
          desc: serviceType === 'transport' ? 'Transport of goods by road' : 'Courier services',
          uqc: 'OTH',
          qty: 0,
          val: 0,
          txval: 0,
          iamt: 0,
          camt: 0,
          samt: 0,
          csamt: 0
        });
      }
      
      const hsnEntry = hsnMap.get(sacCode);
      hsnEntry.qty += 1;
      hsnEntry.val += totalValue;
      hsnEntry.txval += taxableValue;
      hsnEntry.iamt += igst;
      hsnEntry.camt += cgst;
      hsnEntry.samt += sgst;
    }

    // Convert maps to arrays
    for (const [ctin, invoiceList] of b2bMap) {
      b2bInvoices.push({
        ctin: ctin,
        inv: invoiceList
      });
    }

    return {
      gstin: gstin,
      ret_period: retPeriod,
      b2b: b2bInvoices,
      b2cs: Array.from(b2csMap.values()),
      hsn: Array.from(hsnMap.values())
    };
  }

  /**
   * Transform invoices to Whitebook GSTR-3B API format
   * @param {Array} invoices - Array of validated invoices
   * @param {string} gstin - Company GSTIN
   * @param {string} retPeriod - Return period
   * @returns {Object} - Whitebook GSTR-3B formatted data
   */
  async transformToWhitebookGSTR3BFormat(invoices, gstin, retPeriod) {
    const gstr3bData = transformToGSTR3B(invoices, gstin, retPeriod);
    
    // Ensure compliance with Whitebook API format
    return {
      gstin: gstin,
      ret_period: retPeriod,
      ...gstr3bData
    };
  }
}

// Export singleton instance
const gstService = new GSTService();
export default gstService;


